import { Component, OnDestroy } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { CommonModule } from '@angular/common';
import { SpinnerComponent } from './shared/spinner.component';
import { SocketService } from './services/socket/socket.service';
import { NotificationService } from './services/notification/notification.service';
import { Subscription, timer } from 'rxjs';

// Add this interface here
interface SocketMessage {
  status: 'success' | 'fail';
  data: string;
}

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, CommonModule, SpinnerComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.css'
})
export class AppComponent {
    title = 'master';
  check = false;
  blnDBDisConnection = false;
  blnDBConnection = true;

  private socketSub!: Subscription;
  private reconnectInterval = 5000; // 5 seconds

  constructor(
    private socketService: SocketService,
    private notifyService: NotificationService,
    private router: Router,
    
  ) {
    this.initSocketConnection();
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        localStorage.setItem('lastRoute', event.urlAfterRedirects);
      }
    });
   
  }

  /** Initialize socket connection and fallback */
  private initSocketConnection(): void {
    this.connectSocket();

    // Fallback if initial connection fails
    timer(3000).subscribe(() => {
      if (!this.check) {
        this.notifyService.hideInfoToast();
        this.notifyService.hideSuccessToast();
        this.notifyService.showErrorWithTimeout('Error while connecting', '');
      }
    });
  }

  /** Connect socket and subscribe to messages */
  private connectSocket(): void {
    // Send initial test message
    this.socketService.sendMessage('tantra');

    // Show connecting info
    this.notifyService.showInfoWithTimeout('Connecting.....', '');

    // Subscribe to socket messages
    this.socketSub = this.socketService.getMessage().subscribe({
      next: (msg: SocketMessage) => this.handleSocketMessage(msg),
      error: (err) => {
        console.error('Socket error:', err);
        this.reconnectSocket();
      }
    });
  }

  /** Handle incoming socket messages */
  private handleSocketMessage(msg: SocketMessage): void {
    if (!msg || msg.status !== 'success' || !msg.data) return;

    switch (msg.data) {
      case 'Connected':
        this.check = true;
        this.notifyService.hideWarningToast();
        this.notifyService.hideInfoToast();
        this.notifyService.showSuccessWithTimeout('Connected To Server', '');
        this.autoHideToast();
        break;

      case 'Setting database connection':
        if (this.blnDBConnection) {
          this.blnDBDisConnection = true;
          this.blnDBConnection = false;
          this.notifyService.hideErrorToast();
          this.notifyService.hideSuccessToast();
          this.notifyService.showInfoWithTimeout(
            'Reconnecting Database Connection Please Wait..',
            ''
          );
        }
        break;

      case 'database connected':
        if (this.blnDBDisConnection) {
          this.blnDBDisConnection = false;
          this.blnDBConnection = true;
          this.notifyService.hideErrorToast();
          this.notifyService.hideInfoToast();
          this.notifyService.showSuccessWithTimeout('Database Connection Restored', '');
          this.autoHideToast();
        }
        break;

      default:
        console.log('Unhandled socket message:', msg.data);
        break;
    }
  }

  /** Auto-hide success toast after timeout */
  private autoHideToast(timeout: number = 3000): void {
    setTimeout(() => {
      this.notifyService.hideSuccessToast();
    }, timeout);
  }

  /** Attempt socket reconnection */
  private reconnectSocket(): void {
    console.warn(`Attempting to reconnect socket in ${this.reconnectInterval / 1000}s...`);
    setTimeout(() => {
      this.socketSub?.unsubscribe();
      this.connectSocket();
    }, this.reconnectInterval);
  }

  /** Handle user inactivity (optional) */
  public handleInactivityCallback(): void {
    console.log('User inactive or session timeout.');
  }

  /** Cleanup subscriptions on component destroy */
  ngOnDestroy(): void {
    this.socketSub?.unsubscribe();
  }
}
